import { get } from 'lodash/fp';

import { createSlice } from '@reduxjs/toolkit';

import { TIMEKEEPING_STATUSES } from './constants';
import {
  generateStatusData,
  getCalculatedTimecardsOnUpdate,
  getTimecardsWithProcessedUpdates,
} from './helpers';

const updateTotals = (totals, newVal) => ({
  totalHours: totals.totalHours + get(['timecard', 'totalHours'], newVal),
  totalEarnings: totals.totalEarnings + get(['timecard', 'totalEarnings'], newVal),
  overtimeHours: totals.overtimeHours + get(['timecard', 'overtimeHours'], newVal),
  regularHours: totals.regularHours + get(['timecard', 'regularHours'], newVal),
});

const updateTimeCardStatus = (timecards, placements, status) =>
  timecards.map((card) => {
    let updated = {};
    Object.keys(get('days', card)).forEach((key) => {
      updated = {
        ...updated,
        ...{
          [key]: [
            ...get('days', card)[key].map((item) =>
              placements.find((placement) => placement === get(['placement'], item))
                ? { ...item, timecard: { ...item.timecard, status } }
                : item
            ),
          ],
        },
      };
    });

    return { ...card, days: updated };
  });

const setTimeCard = (timecards, newTimeCard) =>
  timecards.map((card) => {
    let updatedDays = {};
    let totals = {
      totalHours: 0,
      totalEarnings: 0,
      overtimeHours: 0,
      regularHours: 0,
    };
    Object.keys(get('days', card)).forEach((key) => {
      updatedDays = {
        ...updatedDays,
        ...{
          [key]: [
            ...get('days', card)[key].map((item) => {
              if (get(['placement'], newTimeCard) === get(['placement'], item)) {
                totals = updateTotals(totals, newTimeCard);
                return newTimeCard;
              }
              totals = updateTotals(totals, item);
              return item;
            }),
          ],
        },
      };
    });

    return {
      ...card,
      ...totals,
      days: updatedDays,
    };
  });

const setSelectedTimeCardOnUpdate = (timecards, selectedTimeCard) => {
  if (timecards && selectedTimeCard) {
    const filterTimecard = timecards.filter(
      (card) => get(['candidate', 'id'], card) === get(['candidate', 'id'], selectedTimeCard)
    );
    const newCard = filterTimecard[0];
    const updatedData = {
      ...newCard,
      ...generateStatusData(newCard, selectedTimeCard),
      id: get(['candidate', 'id'], newCard),
      overtimeHours: get('overtimeHours', newCard),
      regularHours: get('regularHours', newCard),
      totalHours: get('totalHours', newCard),
      employeeName: get(['candidate', 'name'], newCard),
    };

    return {
      ...filterTimecard[0],
      ...updatedData,
    };
  }
  return null;
};

const initialState = {
  timekeepingOverviewData: {},
  timeCardJobOrder: {},
  timecards: [],
  shouldTimekeepingOverviewReload: false,
  timecardUpdateError: null,
  selectedTimeCard: null,
  updatedPlacement: [],
  timeCardIsUpdating: false,
  timecardFilterProps: {},
  timecardsIsProcessing: false,
  timecardActionIsProcessing: false,
  isProcessingBatchApproval: false,
  isUpdatingDailyTimecard: false,
  batchActionError: null,
  dailyTimecards: [],
  weeklyTimecards: [],
  totalWeeklyTimecardsCount: 0,
  totalDailyTimecardsRowCount: 0,
  isWeeklyTimecardsLoading: false,
  isTimekeepingOverviewDataLoading: false,
  isDailyTimecardsLoading: false,
  isDisputedTimecardUpdating: false,
  isTimecardUpdateHoursLoading: false,
  isApproveTimecardsLoading: false,
  isResolveTimecardsLoading: false,
  isFinalizeTimecardsLoading: false,
  isUnFinalizeTimecardsLoading: false,
  isAddLunchTimecardsLoading: false,
  isAddMissingShiftTimecardsLoading: false,
  isReloadDailyTimecards: false,
};
export const timekeepingSlice = createSlice({
  name: 'timekeepingSlice',
  initialState,
  reducers: {
    /* eslint-disable no-param-reassign */
    getTimekeepingOverviewData: (state) => {
      state.isTimekeepingOverviewDataLoading = true;
    },
    getTimekeepingOverviewDataProcessed: (state, action) => {
      state.isTimekeepingOverviewDataLoading = false;
      state.timekeepingOverviewData = action.payload.data;
      state.shouldTimekeepingOverviewReload = false;
    },
    getTimekeepingOverviewDataError: (state) => {
      state.isTimekeepingOverviewDataLoading = false;
      state.shouldTimekeepingOverviewReload = false;
    },
    getTimeCards: (state, action) => {
      state.timecards = [];
      state.timecardsIsProcessing = true;
      state.timecardFilterProps = action.payload;
    },
    getTimeCardsProcessed: (state, action) => {
      state.timecardsIsProcessing = false;
      const { count, documents } = action.payload.data;
      state.timeSheetGridTotalRowCount = count;
      state.timecards = documents;
    },
    getTimeCardsError: (state) => {
      state.timecardsIsProcessing = false;
    },
    getWeeklyTimecards: (state) => {
      state.isWeeklyTimecardsLoading = true;
    },
    getWeeklyTimecardsProcessed: (state, action) => {
      state.isWeeklyTimecardsLoading = false;
      const { documents, count } = action.payload.data;
      state.weeklyTimecards = documents;
      state.totalWeeklyTimecardsCount = count;
    },
    getWeeklyTimecardsError: (state) => {
      state.isWeeklyTimecardsLoading = false;
    },

    getDailyTimecards: (state) => {
      state.isDailyTimecardsLoading = true;
    },
    getDailyTimecardsProcessed: (state, action) => {
      state.isDailyTimecardsLoading = false;
      const { documents, count } = action.payload.data;
      state.totalDailyTimecardsRowCount = count;
      state.dailyTimecards = documents.map((doc) => {
        const { timecard } = doc;
        return {
          placementId: doc._id,
          id: timecard._id,
          name: doc.candidate.name,
          corporation: doc.corporation.name,
          title: doc.jobOrder.name,
          shift: { start: doc.start, end: doc.end },
          punches: timecard?.current?.punches || [],
          status: timecard.status,
          tag: timecard.tag,
          hours: timecard.totalHours,
          start: doc.start,
        };
      });
      state.isReloadDailyTimecards = false;
    },
    getDailyTimecardsError: (state) => {
      state.isDailyTimecardsLoading = false;
    },

    updateDisputedTimecard: (state) => {
      state.isDisputedTimecardUpdating = true;
    },
    updateDisputedTimecardProcessed: (state, action) => {
      state.isDisputedTimecardUpdating = false;
      const document = action?.payload?.data?.document;
      const getCurrentPlacements = state.weeklyTimecards.map((currentItem) =>
        currentItem?.candidate?._id !== document?.candidate?._id
          ? currentItem
          : getCalculatedTimecardsOnUpdate(currentItem, document)
      );

      state.weeklyTimecards = getCurrentPlacements;
      state.shouldTimekeepingOverviewReload = true;
    },
    updateDisputedTimecardError: (state) => {
      state.isDisputedTimecardUpdating = false;
    },

    updateTimecardHours: (state) => {
      state.isTimecardUpdateHoursLoading = true;
    },

    updateTimecardHoursProcessed: (state, action) => {
      const res = action.payload.data;
      state.dailyTimecards = state.dailyTimecards.map((tc) => {
        if (tc.placementId === res._id) {
          return {
            ...tc,
            punches: res?.timecard?.current?.punches,
            hours: res.hours,
          };
        }
        return tc;
      });
      state.isTimecardUpdateHoursLoading = false;
    },
    updateTimecardHoursError: (state) => {
      state.isTimecardUpdateHoursLoading = false;
    },
    approveTimecardsByPlacementIds: (state) => {
      state.isApproveTimecardsLoading = true;
      state.isReloadDailyTimecards = false;
    },
    approveTimecardsByPlacementIdsProcessed: (state, action) => {
      const response = action.payload.data;

      if (response.failed && response.failed.length > 0) {
        state.batchActionError = response.failed[0].messageKey;
        state.isApproveTimecardsLoading = false;
        return;
      }

      // Handle successful updates
      const placements = response.updated || [];
      state.timecards = updateTimeCardStatus(
        state.timecards,
        placements,
        TIMEKEEPING_STATUSES.APPROVED
      );
      state.isApproveTimecardsLoading = false;
      state.isReloadDailyTimecards = true;
    },

    approveTimecardsByPlacementIdsError: (state) => {
      state.isApproveTimecardsLoading = false;
      state.isReloadDailyTimecards = false;
    },
    resolveTimecardsByPlacementIds: (state) => {
      state.isResolveTimecardsLoading = true;
      state.isReloadDailyTimecards = false;
    },
    resolveTimecardsByPlacementIdsProcessed: (state, action) => {
      const response = action.payload.data;

      if (response.failed && response.failed.length > 0) {
        state.batchActionError = response.failed[0].messageKey;
        state.isResolveTimecardsLoading = false;
        return;
      }

      // Handle successful updates
      const placements = response.updated || [];
      state.timecards = updateTimeCardStatus(
        state.timecards,
        placements,
        TIMEKEEPING_STATUSES.RESOLVED
      );
      state.isResolveTimecardsLoading = false;
      state.isReloadDailyTimecards = true;
    },
    resolveTimecardsByPlacementIdsError: (state) => {
      state.isResolveTimecardsLoading = false;
      state.isReloadDailyTimecards = false;
    },
    finalizeTimecardsByPlacementIds: (state) => {
      state.isFinalizeTimecardsLoading = true;
      state.isReloadDailyTimecards = false;
    },
    finalizeTimecardsByPlacementIdsProcessed: (state, action) => {
      const response = action.payload.data;

      if (response.failed && response.failed.length > 0) {
        state.batchActionError = response.failed[0].messageKey;
        state.isFinalizeTimecardsLoading = false;
        return;
      }

      // Handle successful updates
      const placements = response.updated || [];
      state.timecards = updateTimeCardStatus(
        state.timecards,
        placements,
        TIMEKEEPING_STATUSES.FINALIZED
      );
      state.isFinalizeTimecardsLoading = false;
    },
    finalizeTimecardsByPlacementIdsError: (state) => {
      state.isFinalizeTimecardsLoading = false;
      state.isReloadDailyTimecards = false;
    },

    approveTimecardsBatch: (state) => {
      state.isApproveTimecardsLoading = true;
      state.isReloadDailyTimecards = false;
    },
    approveTimecardsBatchProcessed: (state, action) => {
      const response = action.payload.data;

      if (response.failed && response.failed.length > 0) {
        state.batchActionError = response.failed[0].messageKey;
        state.isApproveTimecardsLoading = false;
        return;
      }

      state.batchActionUpdatedItems.push(...(action?.payload?.data?.documents?.updated || []));
      state.batchActionFailedItems.push(...(action?.payload?.data?.documents?.failed || []));
      state.batchActionCount =
        response.count -
        action.payload.data.documents.updated.length -
        action.payload.data.documents.failed.length;

      if (response.documents?.updated?.length > 0) {
        state.weeklyTimecards = getTimecardsWithProcessedUpdates(
          state.weeklyTimecards,
          response.documents.updated
        );
      }

      state.isApproveTimecardsLoading = false;
    },
    approveTimecardsBatchError: (state) => {
      state.isFinalizeTimecardsLoading = false;
      state.isApproveTimecardsLoading = false;
    },

    finalizeTimecardsBatch: (state) => {
      state.isFinalizeTimecardsLoading = true;
      state.isReloadDailyTimecards = false;
    },
    finalizeTimecardsBatchProcessed: (state, action) => {
      const response = action.payload.data;

      if (response.failed && response.failed.length > 0) {
        state.batchActionError = response.failed[0].messageKey;
        state.isFinalizeTimecardsLoading = false;
        return;
      }

      state.batchActionUpdatedItems.push(...(action?.payload?.data?.documents?.updated || []));
      state.batchActionFailedItems.push(...(action?.payload?.data?.documents?.failed || []));
      state.batchActionCount =
        response.count -
        action.payload.data.documents.updated.length -
        action.payload.data.documents.failed.length;

      if (response.documents?.updated?.length > 0) {
        state.weeklyTimecards = getTimecardsWithProcessedUpdates(
          state.weeklyTimecards,
          response.documents.updated
        );
      }

      state.isFinalizeTimecardsLoading = false;
    },
    finalizeTimecardsBatchError: (state) => {
      state.isFinalizeTimecardsLoading = false;
      state.isReloadDailyTimecards = false;
    },

    resolveTimecardsBatch: (state) => {
      state.isResolveTimecardsLoading = true;
      state.isReloadDailyTimecards = false;
    },
    resolveTimecardsBatchProcessed: (state, action) => {
      const response = action.payload.data;

      if (response.failed && response.failed.length > 0) {
        state.batchActionError = response.failed[0].messageKey;
        state.isResolveTimecardsLoading = false;
        return;
      }

      state.batchActionUpdatedItems.push(...(action?.payload?.data?.documents?.updated || []));
      state.batchActionFailedItems.push(...(action?.payload?.data?.documents?.failed || []));
      state.batchActionCount =
        response.count -
        action.payload.data.documents.updated.length -
        action.payload.data.documents.failed.length;

      if (response.documents?.updated?.length > 0) {
        state.weeklyTimecards = getTimecardsWithProcessedUpdates(
          state.weeklyTimecards,
          response.documents.updated
        );
      }

      state.isResolveTimecardsLoading = false;
    },
    resolveTimecardsBatchError: (state) => {
      state.isResolveTimecardsLoading = false;
      state.isReloadDailyTimecards = false;
    },

    unFinalizeTimecardsByPlacementIds: (state) => {
      state.isUnFinalizeTimecardsLoading = true;
      state.isReloadDailyTimecards = false;
    },
    unFinalizeTimecardsByPlacementIdsProcessed: (state) => {
      state.isUnFinalizeTimecardsLoading = false;
      state.isReloadDailyTimecards = true;
    },
    unFinalizeTimecardsByPlacementIdsError: (state) => {
      state.isUnFinalizeTimecardsLoading = false;
      state.isReloadDailyTimecards = false;
    },
    addLunchTimecardsByPlacementIds: (state) => {
      state.isAddLunchTimecardsLoading = true;
      state.isReloadDailyTimecards = false;
    },
    addLunchTimecardsByPlacementIdsProcessed: (state) => {
      state.isAddLunchTimecardsLoading = false;
      state.isReloadDailyTimecards = true;
    },
    addLunchTimecardsByPlacementIdsError: (state) => {
      state.isAddLunchTimecardsLoading = false;
      state.isReloadDailyTimecards = false;
    },
    addMissingShiftTimecardsByPlacementIds: (state) => {
      state.isAddMissingShiftTimecardsLoading = true;
      state.isReloadDailyTimecards = false;
    },
    addMissingShiftTimecardsByPlacementIdsProcessed: (state) => {
      state.isAddMissingShiftTimecardsLoading = false;
      state.isReloadDailyTimecards = true;
    },
    addMissingShiftTimecardsByPlacementIdsError: (state) => {
      state.isAddMissingShiftTimecardsLoading = false;
      state.isReloadDailyTimecards = false;
    },

    getPathSelectedTimeCards: (state) => {
      state.timecardsIsProcessing = true;
    },
    getPathSelectedTimeCardsProcessed: (state, action) => {
      state.timecardsIsProcessing = false;
      const { documents } = action.payload.data;
      const doc = documents[0];

      if (
        doc &&
        state.timecards.length > 0 &&
        doc.candidate.id !== state.timecards[0].candidate.id
      ) {
        state.timecards.unshift(doc);
        state.selectedTimeCard = doc;
      }
    },
    getPathSelectedTimeCardsError: (state) => {
      state.timecardsIsProcessing = false;
    },

    updateTimeCard: (state) => {
      state.timeCardIsUpdating = true;
    },
    updateTimeCardProcessed: (state, action) => {
      const placement = action?.payload?.data?.placement;
      state.timecards = setTimeCard(state.timecards, placement);

      state.selectedTimeCard = setSelectedTimeCardOnUpdate(state.timecards, state.selectedTimeCard);
      state.timeCardIsUpdating = false;
    },
    updateTimeCardError: (state, action) => {
      state.timeCardIsUpdating = false;
      const messageData =
        action?.payload?.errors?.GLOBAL?.messageKey || 'error.generic.something.went.wrong';
      state.timecardUpdateError = messageData;
    },
    clearTimeCardError: (state) => {
      state.timecardUpdateError = null;
    },
    setSelectedTimeCard: (state, action) => {
      state.selectedTimeCard = action.payload;
    },
    postTimeCardApproval: (state) => {
      state.timecardActionIsProcessing = true;
    },
    postTimeCardApprovalProcessed: (state, action) => {
      state.timecardActionIsProcessing = false;
      const placements = action?.payload?.data?.placements;

      state.timecards = updateTimeCardStatus(
        state.timecards,
        placements,
        TIMEKEEPING_STATUSES.APPROVED
      );
      state.selectedTimeCard = setSelectedTimeCardOnUpdate(state.timecards, state.selectedTimeCard);
    },
    postTimeCardApprovalError: (state) => {
      state.timecardActionIsProcessing = false;
    },
    postTimeCardResolve: (state) => {
      state.timecardActionIsProcessing = true;
    },
    postTimeCardResolveProcessed: (state, action) => {
      state.timecardActionIsProcessing = false;
      const placements = action?.payload?.data?.placements;

      state.timecards = updateTimeCardStatus(
        state.timecards,
        placements,
        TIMEKEEPING_STATUSES.RESOLVED
      );
      state.selectedTimeCard = setSelectedTimeCardOnUpdate(state.timecards, state.selectedTimeCard);
    },
    postTimeCardResolveError: (state) => {
      state.timecardActionIsProcessing = false;
    },
    postTimeCardFinalize: (state) => {
      state.timecardActionIsProcessing = false;
    },
    postTimeCardFinalizeProcessed: (state, action) => {
      state.timecardActionIsProcessing = false;
      const placements = action?.payload?.data?.placements;
      state.timecards = updateTimeCardStatus(
        state.timecards,
        placements,
        TIMEKEEPING_STATUSES.FINALIZED
      );
      state.selectedTimeCard = setSelectedTimeCardOnUpdate(state.timecards, state.selectedTimeCard);
    },
    postTimeCardFinalizeError: (state) => {
      state.timecardActionIsProcessing = false;
    },
    postTimeCardUnFinalize: (state) => {
      state.timecardActionIsProcessing = false;
    },
    postTimeCardUnFinalizeProcessed: (state, action) => {
      state.timecardActionIsProcessing = false;
      const placements = action?.payload?.data?.placements;
      state.timecards = updateTimeCardStatus(
        state.timecards,
        placements,
        TIMEKEEPING_STATUSES.RESOLVED
      );
      state.weeklyTimecards = state.weeklyTimecards.map((candidate) => ({
        ...candidate,
        timecards: candidate.timecards.map((t) =>
          placements.includes(t.placementId)
            ? { ...t, timecard: { ...t.timecard, status: TIMEKEEPING_STATUSES.RESOLVED } }
            : t
        ),
      }));
      state.selectedTimeCard = setSelectedTimeCardOnUpdate(state.timecards, state.selectedTimeCard);
    },
    postTimeCardUnFinalizeError: (state) => {
      state.timecardActionIsProcessing = false;
    },
    postTimeCardBatchApproval: (state) => {
      state.isProcessingBatchApproval = true;
    },
    postTimeCardBatchApprovalProcessed: (state) => {
      state.isProcessingBatchApproval = false;
    },
    postTimeCardBatchApprovalError: (state, action) => {
      state.isProcessingBatchApproval = false;
      const messageData =
        action?.payload?.GLOBAL?.messageKey || 'error.generic.something.went.wrong';
      state.batchActionError = messageData;
    },

    updateDailyTimekeepingPunches: (state) => {
      state.isUpdatingDailyTimecard = true;
    },
    updateDailyTimekeepingPunchesProcessed: (state, action) => {
      const placement = action?.payload?.data?.placement;
      const placementId = placement.placement;
      const { punches } = placement.timecard;
      const totalHours = placement?.timecard?.totalHours;

      state.dailyTimecards = state.dailyTimecards.map((tc) => {
        if (tc.placementId === placementId) {
          return {
            ...tc,
            punches,
            hours: totalHours,
          };
        }
        return tc;
      });
      state.isUpdatingDailyTimecard = false;
    },
    updateDailyTimekeepingPunchesError: (state) => {
      state.isUpdatingDailyTimecard = false;
    },
    timecardEditV2: (state) => {
      state.isUpdatingDailyTimecard = true;
    },
    timecardEditV2Processed: (state, action) => {
      state.isUpdatingDailyTimecard = false;
      const document = action.payload?.data?.document;
      const getCurrentPlacements = state.weeklyTimecards.map((currentItem) =>
        currentItem?.candidate?._id !== document?.candidate?._id
          ? currentItem
          : getCalculatedTimecardsOnUpdate(currentItem, document)
      );

      state.weeklyTimecards = getCurrentPlacements;
      state.shouldTimekeepingOverviewReload = true;
    },
    timecardEditV2Error: (state) => {
      state.isUpdatingDailyTimecard = false;
    },
    updateTimekeepingDepartments: (state) => {
      state.isUpdatingDepartments = true;
    },
    updateTimekeepingDepartmentsProcessed: (state) => {
      state.isUpdatingDepartments = false;
    },
    updateTimekeepingDepartmentsError: (state) => {
      state.isUpdatingDepartments = false;
    },
  },
});

export const timekeepingDeprecatedReducer = timekeepingSlice.reducer;

export const {
  getCorporationsForTimekeepingProcessed,
  getCorporationsForTimekeepingError,
  getTimekeepingOverviewData,
  getTimekeepingOverviewDataProcessed,
  getTimekeepingOverviewDataError,
  getTimeCards,
  getTimeCardsProcessed,
  getTimeCardsError,
  getDailyTimecards,
  getDailyTimecardsProcessed,
  getWeeklyTimecards,
  getWeeklyTimecardsProcessed,
  getWeeklyTimecardsError,
  getDailyTimecardsError,
  updateDisputedTimecard,
  updateDisputedTimecardProcessed,
  updateDisputedTimecardError,
  updateTimecardHours,
  updateTimecardHoursProcessed,
  updateTimecardHoursError,
  approveTimecardsByPlacementIds,
  approveTimecardsByPlacementIdsProcessed,
  approveTimecardsByPlacementIdsError,
  resolveTimecardsByPlacementIds,
  resolveTimecardsByPlacementIdsProcessed,
  resolveTimecardsByPlacementIdsError,
  finalizeTimecardsByPlacementIds,
  finalizeTimecardsByPlacementIdsProcessed,
  finalizeTimecardsByPlacementIdsError,
  finalizeTimecardsBatch,
  finalizeTimecardsBatchProcessed,
  finalizeTimecardsBatchError,
  approveTimecardsBatch,
  approveTimecardsBatchProcessed,
  approveTimecardsBatchError,
  resolveTimecardsBatch,
  resolveTimecardsBatchProcessed,
  resolveTimecardsBatchError,
  unFinalizeTimecardsByPlacementIds,
  unFinalizeTimecardsByPlacementIdsProcessed,
  unFinalizeTimecardsByPlacementIdsError,
  addLunchTimecardsByPlacementIds,
  addLunchTimecardsByPlacementIdsProcessed,
  addLunchTimecardsByPlacementIdsError,
  addMissingShiftTimecardsByPlacementIds,
  addMissingShiftTimecardsByPlacementIdsProcessed,
  addMissingShiftTimecardsByPlacementIdsError,
  getPathSelectedTimeCards,
  getPathSelectedTimeCardsProcessed,
  getPathSelectedTimeCardsError,
  updateTimeCard,
  updateTimeCardProcessed,
  updateTimeCardError,
  clearTimeCardError,
  setSelectedTimeCard,
  postTimeCardApproval,
  postTimeCardApprovalError,
  postTimeCardApprovalProcessed,
  postTimeCardResolve,
  postTimeCardResolveProcessed,
  postTimeCardResolveError,
  postTimeCardFinalize,
  postTimeCardFinalizeProcessed,
  postTimeCardFinalizeError,
  postTimeCardUnFinalize,
  postTimeCardUnFinalizeProcessed,
  postTimeCardUnFinalizeError,
  postTimeCardBatchApproval,
  postTimeCardBatchApprovalProcessed,
  postTimeCardBatchApprovalError,
  updateDailyTimekeepingPunches,
  updateDailyTimekeepingPunchesProcessed,
  updateDailyTimekeepingPunchesError,
  timecardEditV2,
  timecardEditV2Processed,
  timecardEditV2Error,
  updateTimekeepingDepartments,
  updateTimekeepingDepartmentsProcessed,
  updateTimekeepingDepartmentsError,
  clearBatchActionErrorAndCount,
} = timekeepingSlice.actions;
